import { queryKeys } from '@/lib/query-keys';
import { adminSyncClient } from '@/lib/api/ts-rest';
import { createLogger } from '@/lib/logger';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

const log = createLogger('useSyncMonitoringActions');

export const useTriggerLinesSync = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (organizationId: string) => {
      const res = await adminSyncClient.runLinesSync({
        body: { organizationId },
      });
      if (res.status !== 201) throw new Error('Failed to run sync');
      return res.body;
    },
    onSuccess: (data) => {
      const errorsLabel = data.errors > 0 ? `, ${data.errors} erreur(s)` : '';
      toast.success(
        `Sync lignes OK : ${data.upserted} ligne(s) upsertée(s), ${data.softDeleted} soft-delete(s)${errorsLabel} en ${data.durationMs}ms`
      );
      queryClient.invalidateQueries({ queryKey: queryKeys.adminSync.root() });
    },
    onError: (error) => {
      toast.error('Erreur lors du sync des lignes');
      log.error('Trigger lines sync error', error);
    },
  });
};

export const useTriggerLinesFullSync = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const res = await adminSyncClient.runLinesFullSync();
      if (res.status !== 201) throw new Error('Failed to run full sync');
      return res.body;
    },
    onSuccess: (data) => {
      const errorsLabel = data.errors > 0 ? `, ${data.errors} erreur(s)` : '';
      const failedLabel =
        data.failedOrganizations > 0
          ? `, ${data.failedOrganizations} org(s) en échec`
          : '';
      const seconds = (data.durationMs / 1000).toFixed(1);
      toast.success(
        `Sync lignes global OK : ${data.organizationsCount} org(s), ${data.upserted} upsert(s), ${data.softDeleted} soft-delete(s)${errorsLabel}${failedLabel} en ${seconds}s`
      );
      queryClient.invalidateQueries({ queryKey: queryKeys.adminSync.root() });
    },
    onError: (error) => {
      toast.error('Erreur lors du sync global des lignes');
      log.error('Trigger lines full sync error', error);
    },
  });
};

export const useTriggerDetailsSync = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (organizationId: string) => {
      const res = await adminSyncClient.runDetailsSync({
        body: { organizationId },
      });
      if (res.status !== 201) throw new Error('Failed to run sync');
      return res.body;
    },
    onSuccess: (data) => {
      const nameLabel = data.organizationNameUpdated ? ', nom mis à jour' : '';
      toast.success(
        `Sync orgs & sites OK : ${data.sitesUpdated} site(s) mis à jour, ${data.sitesCreated} créé(s)${nameLabel}`
      );
      queryClient.invalidateQueries({ queryKey: queryKeys.adminSync.root() });
    },
    onError: (error) => {
      toast.error('Erreur lors du sync des détails');
      log.error('Trigger details sync error', error);
    },
  });
};

export const useTriggerDetailsFullSync = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const res = await adminSyncClient.runDetailsFullSync();
      if (res.status !== 201) throw new Error('Failed to run full sync');
      return res.body;
    },
    onSuccess: (data) => {
      const failedLabel =
        data.failedOrganizations > 0
          ? `, ${data.failedOrganizations} org(s) en échec`
          : '';
      const seconds = (data.durationMs / 1000).toFixed(1);
      toast.success(
        `Sync orgs & sites global OK : ${data.organizationsCount} org(s), ${data.organizationNamesUpdated} nom(s) mis à jour, ${data.sitesUpdated} site(s) mis à jour, ${data.sitesCreated} créé(s)${failedLabel} en ${seconds}s`
      );
      queryClient.invalidateQueries({ queryKey: queryKeys.adminSync.root() });
    },
    onError: (error) => {
      toast.error('Erreur lors du sync global des détails');
      log.error('Trigger details full sync error', error);
    },
  });
};
